import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { injectIntl } from 'react-intl';
import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';
import format from 'date-fns/format';

import intlArrayHelper from 'common/language/intlArrayHelper';

const DateDropdown = ({ id, name, className, maxDate, minDate, dateFormat, onChange, showToolTip, tooltipContent, value, intl, readOnly, answerRequired, describedBy, monthYearOnly }) => {
  const months = intlArrayHelper('questionnaire.date.dropdown');
  const convertToDate = date => (date !== null && date !== 'Invalid date' && isValid(parse(date, dateFormat, new Date())) ? parse(date, dateFormat, new Date()) : null);
  const addTrailingZero = day => (day < 10 ? `0${ day }` : `${ day }`);
  const [selectedDay, setSelectedDay] = useState(value ? convertToDate(value)?.getDate() : null);
  const [selectedYear, setSelectedYear] = useState(value ? convertToDate(value)?.getFullYear() : null);
  const [selectedMonth, setSelectedMonth] = useState(value ? months[convertToDate(value)?.getMonth()] : null);
  const [minimumDate, setMinimumDate] = useState(convertToDate(minDate));
  const [maximumDate, setMaximumDate] = useState(convertToDate(maxDate));
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const years = Array.from(new Array(currentYear && minimumDate ? currentYear - minimumDate.getFullYear() : 100), (val, index) => currentYear - index);
  const [fullDate, setFullDate] = useState(value !== null ? value : null);

  const getDaysArray = (year, month) => {
    const yearSelected = year !== null ? year : currentYear;
    const monthIndex = month !== null ? month + 1 : currentMonth + 1;

    const dateObject = new Date(yearSelected, monthIndex, 0);
    return Array.from({ length: dateObject.getDate() }, (_, i) => i + 1);
  };

  const [days, setDays] = useState(Array.from({ length: 31 }, (_, i) => i + 1));

  const setValidDate = (year, month, day) => {
    // In month/year-only mode the day is not collected; default it to the 1st.
    const effectiveDay = monthYearOnly ? 1 : day;
    if (!year || (month === null || month.length <= 0) || !effectiveDay) {
      if (fullDate) {
        onChange(null);
        setFullDate(null);
      }
      return;
    }

    const date = new Date(year, month, effectiveDay);

    if (maximumDate !== null && date > maximumDate) {
      if (fullDate) {
        onChange(null);
        setFullDate(null);
      }
      return;
    }

    if (minimumDate !== null && date < minimumDate) {
      if (fullDate) {
        onChange(null);
        setFullDate(null);
      }
      return;
    }

    onChange(format(date, dateFormat));
    setFullDate(format(date, dateFormat));
  };

  const changeYear = (year) => {
    const monthIndex = selectedMonth !== null ? months.findIndex(val => val.value === selectedMonth.value) : null;
    const dayArray = getDaysArray(year, monthIndex);
    let day = selectedDay;
    setDays(dayArray);
    if (selectedDay && !dayArray.includes(parseInt(selectedDay, 10))) {
      setSelectedDay(null);
      day = null;
    }
    setSelectedYear(year);
    setValidDate(year, monthIndex, day);
  };

  const changeMonth = (month) => {
    const monthIndex = month ? months.findIndex(val => val.value === month) : '';
    const dayArray = getDaysArray(selectedYear, monthIndex);
    let day = selectedDay;
    setDays(dayArray);
    if (selectedDay && !dayArray.includes(parseInt(selectedDay, 10))) {
      setSelectedDay(null);
      day = null;
    }
    setSelectedMonth(months[monthIndex] ?? null);
    setValidDate(selectedYear, monthIndex, day);
  };

  const changeDay = (day) => {
    setSelectedDay(day);
    const monthIndex = selectedMonth !== null ? months.findIndex(val => val.value === selectedMonth.value) : null;
    setValidDate(selectedYear, monthIndex, day);
  };

  const isValidDay = (day) => {
    if (selectedYear === null || selectedMonth === null) {
      return true;
    }

    const selectedDate = new Date(selectedYear, months.findIndex(val => val.value === selectedMonth.value), day);

    if ((maximumDate !== null && selectedDate > maximumDate) || (minimumDate !== null && selectedDate < minimumDate)) {
      if (day === selectedDay) {
        setSelectedDay(null);
      }
      return false;
    }

    return true;
  };

  const isValidMonth = (month) => {
    if (selectedYear === null) {
      return true;
    }

    const selectedDate = new Date(selectedYear, months.findIndex(val => val.value === month), 1);
    const selectedmonthEndDate = new Date(selectedYear, months.findIndex(val => val.value === month) + 1, 0);

    if ((maximumDate !== null && selectedDate > maximumDate) || (minimumDate !== null && selectedmonthEndDate < minimumDate)) {
      if (month === selectedMonth) {
        setSelectedMonth(null);
      }
      return false;
    }

    return true;
  };

  const isValidYear = (year) => {
    if (minimumDate !== null && year < minimumDate.getFullYear()) {
      return false;
    }

    return true;
  };

  const calculateAge = (birthday) => {
    const todaysDate = new Date();
    const dateOfBirth = convertToDate(birthday);
    if (!dateOfBirth) {
      return '';
    }
    const diff = todaysDate.getTime() - dateOfBirth.getTime();
    const numberOfYears = Math.floor(diff / 31556736000);
    const daysDifference = Math.floor((diff % 31556736000) / 86400000);
    const numberOfMonths = Math.floor(daysDifference / 30.4167);

    let dayText = 'day';
    if (numberOfYears < 1 && numberOfMonths < 1) {
      dayText = daysDifference > 1 ? intl.formatMessage({ id: 'questionnaire.dateDropDown.day' }) : intl.formatMessage({ id: 'questionnaire.dateDropDown.days' });

      return `${ daysDifference } ${ dayText }`;
    }

    let monthText = 'month';
    if (numberOfYears < 1) {
      monthText = numberOfMonths > 1 ? intl.formatMessage({ id: 'questionnaire.dateDropDown.months' }) : intl.formatMessage({ id: 'questionnaire.dateDropDown.month' });
      return `${ numberOfMonths } ${ monthText }`;
    }

    let yearText = intl.formatMessage({ id: 'questionnaire.dateDropDown.year' });
    if (numberOfYears > 1) {
      yearText = intl.formatMessage({ id: 'questionnaire.dateDropDown.years' });
    }
    return `${ numberOfYears } ${ yearText }`;
  };

  useEffect(() => {
    const newMaxDate = convertToDate(maxDate);
    setMaximumDate(newMaxDate);
    setCurrentYear(newMaxDate !== null ? newMaxDate.getFullYear() : new Date().getFullYear());
    setCurrentMonth(newMaxDate !== null ? newMaxDate.getMonth() : new Date().getMonth());
  }, [maxDate]);

  useEffect(() => {
    setMinimumDate(convertToDate(minDate));
  }, [minDate]);

  useEffect(() => {
    if (value === fullDate) {
      return;
    }
    const date = convertToDate(value);
    setSelectedDay(date?.getDate() ?? null);
    setSelectedYear(date?.getFullYear() ?? null);
    setSelectedMonth(months[date?.getMonth()] ?? null);
    setFullDate(value ?? null);
    setDays(getDaysArray(date?.getFullYear(), date?.getMonth()));
  }, [value]);

  return (
    <div id={ `dropdown-wrapper-${ id }` } name={ name !== null ? `-dropdown-wrapper-${ name }` : '' } className={ `date-dropdown row ${ className }` }>
      {/* add prefix to id and name */}
      { !monthYearOnly && <div className="col-4 col-md-3 pr-1">
        <select
          name="days"
          id={ `${ id }-day` }
          value={ selectedDay === null ? '' : selectedDay }
          onChange={ e => changeDay(e.target.value) }
          disabled={ readOnly }
          aria-required={ answerRequired && !readOnly ? 'true' : undefined }
          aria-describedby={ describedBy }
          required={ answerRequired && !readOnly }
        >
          <option value="">{intl.formatMessage({ id: 'questionnaire.dateDropDown.shortDateFormat' })}</option>
          {days.map((day, index) => isValidDay(day) && <option key={ `item - ${ day }` } value={ day }>
              {addTrailingZero(day)}
          </option>)}
        </select>
      </div> }
      <div className="col-4 col-md-3 pl-1 pr-1">
        <select
          name="months"
          id={ `${ id }-month` }
          value={ selectedMonth === null ? '' : selectedMonth.value }
          onChange={ e => changeMonth(e.target.value) }
          disabled={ readOnly }
          aria-required={ answerRequired && !readOnly ? 'true' : undefined }
          aria-describedby={ describedBy }
          required={ answerRequired && !readOnly }
        >
          <option value="">{intl.formatMessage({ id: 'questionnaire.dateDropDown.shortMonthFormat' })}</option>
          { months.map((month, index) => isValidMonth(month.value) && <option key={ `item - ${ month.label }` } value={ month.value }>
            { month.label }
          </option>)}
        </select>
      </div>
      <div className="col-4 col-md-3 pl-1">
        <select
          name="years"
          id={ `${ id }-year` }
          value={ selectedYear === null ? '' : selectedYear }
          onChange={ e => changeYear(e.target.value) }
          disabled={ readOnly }
          aria-required={ answerRequired && !readOnly ? 'true' : undefined }
          aria-describedby={ describedBy }
          required={ answerRequired && !readOnly }
        >
          <option value="">{intl.formatMessage({ id: 'questionnaire.dateDropDown.shortYearFormat' })}</option>
          { years.map((yearItem, index) => isValidYear(yearItem) && <option key={ `item - ${ yearItem }` } value={ yearItem }>
            { yearItem }
          </option>) }
        </select>
      </div>
      { showToolTip && fullDate
      && <div className="col-12 col-md-3 tooltip"><strong>{ tooltipContent === 'date' ? 'Date: ' : intl.formatMessage({ id: 'questionnaire.dateDropDown.age' })}</strong> { tooltipContent === 'date' ? fullDate : calculateAge(fullDate) }</div> }
      <input id={ `${ id }-dropdown-value` } name={ `${ name }` } type="hidden" value={ fullDate === null ? '' : fullDate } />
    </div>
  );
};

DateDropdown.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  maxDate: PropTypes.string,
  minDate: PropTypes.string,
  dateFormat: PropTypes.string,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  showToolTip: PropTypes.bool,
  tooltipContent: PropTypes.string,
  value: PropTypes.string,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired,
  readOnly: PropTypes.bool,
  answerRequired: PropTypes.bool,
  describedBy: PropTypes.string,
  monthYearOnly: PropTypes.bool
};

DateDropdown.defaultProps = {
  name: null,
  maxDate: null,
  minDate: null,
  dateFormat: 'MM-dd-yyyy',
  showToolTip: true,
  className: null,
  tooltipContent: null,
  value: null,
  readOnly: false,
  answerRequired: false,
  describedBy: null,
  monthYearOnly: false
};

export default injectIntl(DateDropdown);
